<template>
  <div class="gestione-asporto">
    <div class="header">
      <a href="#" class="btn-back" @click.prevent="goBack">← {{ $t('gestionale') }}</a>
      <h1>{{ $t('gestioneAsporto') }}</h1>
      <div class="header-actions">
        <button class="btn-create" @click="goToCreateOrder">⊕ {{ $t('aggiungi_piatti') }}</button>
        <button class="btn-settings" @click="showSettings = true">⚙ {{ $t('prenotazioniSetting') }}</button>
        <button class="btn-refresh" @click="fetchOrdini">↻ {{ $t('aggiorna') }}</button>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters">
      <select v-model="filtroStato" @change="fetchOrdini">
        <option value="">{{ $t('tutti_stati') }}</option>
        <option value="pagato">{{ $t('nuovi_pagati') }}</option>
        <option value="in_preparazione">{{ $t('preparazione') }}</option>
        <option value="pronto">{{ $t('pronto') }}</option>
        <option value="consegnato">{{ $t('consegnato') }}</option>
      </select>
      
      <div class="date-filter">
        <label for="date-filter">{{ $t('data') }}:</label>
        <input 
          type="date" 
          id="date-filter" 
          v-model="dataFiltro" 
          @change="fetchOrdini"
        />
      </div>
    </div>

    <!-- Orders List -->
    <div class="orders-list" v-if="ordini.length">
      <div v-for="ordine in ordini" :key="ordine._id" class="order-card" :class="'status-' + ordine.stato">
        <div class="order-header">
          <div class="order-info">
            <h3>{{ ordine.nome }}</h3>
            <span class="contact">{{ ordine.email }}</span><br/>
            <span class="contact">{{ ordine.telefono }}</span>
            <div class="ritiro-info" v-if="ordine.dataRitiro">
              📅 <strong>{{ $t('ritiro') }}:</strong> {{ formatDataRitiro(ordine.dataRitiro) }} {{ $t('alle') || 'alle' }} {{ ordine.oraRitiro }}
            </div>
            <div class="delivery-info" v-if="ordine.isDelivery">
              📍 <strong>{{ $t('indirizzo_consegna') || 'Indirizzo' }}:</strong> {{ ordine.via }} {{ ordine.numeroCivico }}, {{ ordine.cap }}<br/>
              🔔 <strong>{{ $t('campanello') || 'Campanello' }}:</strong> {{ ordine.campanello }}
            </div>
          </div>
          <div class="order-badges">
            <span v-if="ordine.isDelivery" class="badge-delivery">{{ $t('consegna_a_domicilio') || 'DELIVERY' }}</span>
            <span v-else class="badge-asporto">{{ $t('asporto') || 'ASPORTO' }}</span>
            <span v-if="ordine.isStaff" class="badge-staff">{{ $t('staff') }}</span>
            <span v-if="!ordine.pagato" class="badge-unpaid">{{ $t('non_associato') }}</span>
            <span class="order-time">{{ formatDate(ordine.tm) }}</span>
          </div>
        </div>

        <div class="order-items">
          <div v-for="(item, idx) in ordine.ordinazione" :key="idx" class="item-row">
            <span class="qty">{{ item.quantita }}x</span>
            <span class="name">{{ item.piatto }}</span>
            <div class="extras" v-if="item.aggiunta && item.aggiunta.length">
              + {{ item.aggiunta.map(a => a.descrizione || a).join(', ') }}
            </div>
            <div class="extras without" v-if="item.senza && item.senza.length">
              - {{ item.senza.join(', ') }}
            </div>
            <div class="extras note" v-if="item.nota" style="color: #6c757d; font-style: italic;">
              {{ $t('nota', 'Nota') }}: {{ item.nota }}
            </div>
          </div>
        </div>

    <div class="order-footer">
          <div class="total">
            {{ $t('totale') }}: {{(ordine.totale / 100).toFixed(2)}}€
          </div>
          
          <div class="actions">
            <div v-if="editingOrder === ordine._id" class="status-edit">
              <select 
                :value="ordine.stato" 
                @change="updateStatus(ordine._id, $event.target.value)"
                @blur="editingOrder = null"
                class="status-select"
                ref="statusSelect"
              >
                <option value="pagato">{{ $t('nuovo') || 'NUOVO' }}</option>
                <option value="in_preparazione">{{ $t('preparazione') }}</option>
                <option value="pronto">{{ $t('pronto') }}</option>
                <option value="consegnato">{{ $t('consegnato') }}</option>
              </select>
            </div>
            
            <div 
              v-else 
              class="current-status clickable" 
              :class="ordine.stato"
              @click="startEditing(ordine)"
              title="Clicca per cambiare stato"
            >
              {{ formatStatus(ordine.stato) }}
              <span class="edit-hint">✎</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <div v-else class="no-orders">
      <p>Nessun ordine trovato</p>
    </div>

    <div class="error-message" v-if="errorMessage">{{ errorMessage }}</div>

    <!-- Settings Modal -->
    <div class="modal-overlay" v-if="showSettings" @click.self="showSettings = false">
      <div class="modal-content settings-modal">
        <div class="modal-header">
          <h2>Impostazioni Orari Asporto</h2>
          <button class="close-btn" @click="showSettings = false">&times;</button>
        </div>
        <div class="modal-body">
          <div class="day-selector-section">
            <label class="giorno-label"><strong>Giorno della Settimana:</strong></label>
            <select v-model="selectedGiorno" class="giorno-select">
              <option value="all">Tutti i giorni (Predefinito)</option>
              <option value="1">Lunedì</option>
              <option value="2">Martedì</option>
              <option value="3">Mercoledì</option>
              <option value="4">Giovedì</option>
              <option value="5">Venerdì</option>
              <option value="6">Sabato</option>
              <option value="0">Domenica</option>
            </select>
          </div>

          <div v-if="selectedGiorno !== 'all'" class="giorno-attivo-row">
            <label class="checkbox-label">
              <input type="checkbox" v-model="currentGiornoSetting.attivo" />
              <span>Asporto attivo il {{ getGiornoName(selectedGiorno) }}</span>
            </label>
          </div>

          <div class="settings-grid" :class="{ 'disabled-grid': selectedGiorno !== 'all' && !currentGiornoSetting.attivo }">
            <div class="setting-group">
              <h3>Pranzo</h3>
              <div class="input-row">
                <label>Inizio:</label>
                <input type="time" v-model="currentGiornoSetting.asportoPranzoStart">
              </div>
              <div class="input-row">
                <label>Fine:</label>
                <input type="time" v-model="currentGiornoSetting.asportoPranzoEnd">
              </div>
            </div>
            <div class="setting-group">
              <h3>Cena</h3>
              <div class="input-row">
                <label>Inizio:</label>
                <input type="time" v-model="currentGiornoSetting.asportoCenaStart">
              </div>
              <div class="input-row">
                <label>Fine:</label>
                <input type="time" v-model="currentGiornoSetting.asportoCenaEnd">
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn-save" @click="saveSettings">Salva Impostazioni</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { store } from '../store/store.js'
import newOrderSound from '@/assets/newOrder.mp3'
const { ipcRenderer } = window.require ? window.require('electron') : { ipcRenderer: null };

export default {
  name: 'GestioneAsporto',
  data() {
    return {
      store,
      ordini: [],
      filtroStato: '',
      dataFiltro: new Date().toISOString().split('T')[0], // Default to today
      errorMessage: '',
      editingOrder: null,
      showSettings: false,
      selectedGiorno: 'all',
      asportoSettings: {
        codicesala: 'asporto',
        asportoPranzoStart: '12:00',
        asportoPranzoEnd: '15:00',
        asportoCenaStart: '19:00',
        asportoCenaEnd: '23:00',
        giorni: {
          '1': { attivo: true, asportoPranzoStart: '12:00', asportoPranzoEnd: '15:00', asportoCenaStart: '19:00', asportoCenaEnd: '23:00' },
          '2': { attivo: true, asportoPranzoStart: '12:00', asportoPranzoEnd: '15:00', asportoCenaStart: '19:00', asportoCenaEnd: '23:00' },
          '3': { attivo: true, asportoPranzoStart: '12:00', asportoPranzoEnd: '15:00', asportoCenaStart: '19:00', asportoCenaEnd: '23:00' },
          '4': { attivo: true, asportoPranzoStart: '12:00', asportoPranzoEnd: '15:00', asportoCenaStart: '19:00', asportoCenaEnd: '23:00' },
          '5': { attivo: true, asportoPranzoStart: '12:00', asportoPranzoEnd: '15:00', asportoCenaStart: '19:00', asportoCenaEnd: '23:00' },
          '6': { attivo: true, asportoPranzoStart: '12:00', asportoPranzoEnd: '15:00', asportoCenaStart: '19:00', asportoCenaEnd: '23:00' },
          '0': { attivo: true, asportoPranzoStart: '12:00', asportoPranzoEnd: '15:00', asportoCenaStart: '19:00', asportoCenaEnd: '23:00' }
        }
      }
    }
  },
  computed: {
    currentGiornoSetting() {
      if (this.selectedGiorno === 'all') {
        return this.asportoSettings
      }
      const dayKey = String(this.selectedGiorno)
      const giorniMap = this.asportoSettings.giorni || {}
      return giorniMap[dayKey] || {
        attivo: true,
        asportoPranzoStart: this.asportoSettings.asportoPranzoStart || '12:00',
        asportoPranzoEnd: this.asportoSettings.asportoPranzoEnd || '15:00',
        asportoCenaStart: this.asportoSettings.asportoCenaStart || '19:00',
        asportoCenaEnd: this.asportoSettings.asportoCenaEnd || '23:00'
      }
    }
  },
  methods: {
    goBack() {
      this.$router.push({ name: 'Gestionale' })
    },
    goToCreateOrder() {
      this.$router.push({ name: 'OrdineAsporto', query: { mode: 'staff' } })
    },
    startEditing(ordine) {
      this.editingOrder = ordine._id
      this.$nextTick(() => {
        const selects = this.$refs.statusSelect
        if (selects) {
          const el = Array.isArray(selects) ? selects[0] : selects
          el?.focus()
        }
      })
      console.log('Start editing:', ordine._id, 'Status:', ordine.stato)
    },
    async fetchOrdini() {
      try {
        const payload = {
          headers: {
            authorization: `Bearer ${JSON.parse(localStorage.token)}`
          }
        }

        if (this.filtroStato) {
          payload.stato = this.filtroStato
        }

        if (this.dataFiltro) {
          payload.data = this.dataFiltro
        }

        const response = await this.axios.post(
          this.store.path + 'getOrdiniAsporto',
          payload
        )

        if (response.data.messaggio) {
          // Sort: 'consegnato' orders first (on the left), then by pickup date/time descending
          this.ordini = response.data.messaggio.sort((a, b) => {
            // Prioritize 'consegnato' status
            if (a.stato === 'consegnato' && b.stato !== 'consegnato') return -1
            if (a.stato !== 'consegnato' && b.stato === 'consegnato') return 1

            // Fallback to pickup date and time descending
            const dateA = `${a.dataRitiro}T${a.oraRitiro}`
            const dateB = `${b.dataRitiro}T${b.oraRitiro}`
            return dateB.localeCompare(dateA)
          })
          this.printNewOrders()
        }
      } catch (error) {
        console.error('Error fetching orders:', error)
        this.errorMessage = 'Errore nel recupero degli ordini'
      }
    },
    async updateStatus(id, nuovoStato) {
      console.log('Updating status to:', nuovoStato)
      // keep editing active until success to prevent UI jump or confirm update
      try {
        await this.axios.post(this.store.path + 'aggiornaStatoAsporto', {
          id,
          stato: nuovoStato,
          headers: {
            authorization: `Bearer ${JSON.parse(localStorage.token)}`
          }
        })
        
        this.editingOrder = null 
        await this.fetchOrdini()
      } catch (error) {
        console.error('Error updating status:', error)
        this.errorMessage = 'Impossibile aggiornare lo stato'
        this.editingOrder = null
      }
    },
    async printNewOrders() {

      if (!ipcRenderer) return


      const today = new Date().toISOString().split('T')[0]

      const newOrders = this.ordini.filter(o => 
        !o.isPrinted && (
          (o.pagato && o.dataRitiro === today) || 
          o.isStaff
        )
      )


      if (newOrders.length === 0) return

      // emetto un suono, l'audio si trova in assets/newOrder.mp3
      const audio = new Audio(newOrderSound)
      audio.play()

      try {
        // Fetch printer settings for "asporto"
        const printerRes = await this.axios.post(this.store.path + 'displaySpecificPrinter', {
          nome: 'asporto'
        }, {
          headers: {
            authorization: `Bearer ${JSON.parse(localStorage.token)}`
          }
        })

        const printers = printerRes.data.messaggio
        if (!printers || !printers.length) {
          console.warn('No "asporto" printer found')
          return
        }

        const printer = printers[0]

        for (const ordine of newOrders) {
          console.log('Printing takeaway order:', ordine._id)
          
          const sanitizedOrder = JSON.parse(JSON.stringify({
            ord: ordine.ordinazione.map(item => ({
              ...item,
              piatto: item.piatto,
              aggiunta: item.aggiunta || [],
              senza: item.senza || [],
              prezzo: item.prezzo,
              countGroup: item.quantita
            })),
            nome: ordine.nome,
            email: ordine.email,
            telefono: ordine.telefono,
            dataRitiro: ordine.dataRitiro,
            oraRitiro: ordine.oraRitiro,
            totale: ordine.totale / 100
          }))

          ipcRenderer.send('print', sanitizedOrder, { 
            ip: printer.ip, 
            porta: printer.porta 
          }, 'asporto')

          await this.axios.post(this.store.path + 'segnaAsportoStampato', {
            id: ordine._id
          }, {
            headers: {
              authorization: `Bearer ${JSON.parse(localStorage.token)}`
            }
          })
          
          ordine.isPrinted = true
        }
      } catch (error) {
        console.error('Error during automatic printing:', error)
      }
    },
    getGiornoName(dayKey) {
      const names = {
        '1': 'Lunedì',
        '2': 'Martedì',
        '3': 'Mercoledì',
        '4': 'Giovedì',
        '5': 'Venerdì',
        '6': 'Sabato',
        '0': 'Domenica'
      }
      return names[String(dayKey)] || ''
    },
    async fetchSettings() {
      try {
        const response = await this.axios.get(this.store.path + 'prenotazioni-setting', {
          headers: { authorization: `Bearer ${JSON.parse(localStorage.token)}` }
        })
        const setting = response.data.find(s => s.codicesala === 'asporto')
        if (setting) {
          const defaultStartP = setting.asportoPranzoStart || '12:00'
          const defaultEndP = setting.asportoPranzoEnd || '15:00'
          const defaultStartC = setting.asportoCenaStart || '19:00'
          const defaultEndC = setting.asportoCenaEnd || '23:00'
          const mergedGiorni = { ...this.asportoSettings.giorni, ...(setting.giorni || {}) }
          
          ;['0', '1', '2', '3', '4', '5', '6'].forEach(dayKey => {
            if (!mergedGiorni[dayKey]) {
              mergedGiorni[dayKey] = {
                attivo: true,
                asportoPranzoStart: defaultStartP,
                asportoPranzoEnd: defaultEndP,
                asportoCenaStart: defaultStartC,
                asportoCenaEnd: defaultEndC
              }
            }
          })

          this.asportoSettings = {
            codicesala: 'asporto',
            asportoPranzoStart: defaultStartP,
            asportoPranzoEnd: defaultEndP,
            asportoCenaStart: defaultStartC,
            asportoCenaEnd: defaultEndC,
            ...setting,
            giorni: mergedGiorni
          }
        }
      } catch (error) {
        console.error('Error fetching asporto settings:', error)
      }
    },
    async saveSettings() {
      try {
        await this.axios.post(this.store.path + 'prenotazioni-setting', this.asportoSettings, {
          headers: { authorization: `Bearer ${JSON.parse(localStorage.token)}` }
        })
        this.showSettings = false
        this.errorMessage = 'Impostazioni salvate con successo'
        setTimeout(() => { if(this.errorMessage.includes('successo')) this.errorMessage = '' }, 3000)
      } catch (error) {
        console.error('Error saving asporto settings:', error)
        alert('Errore nel salvataggio delle impostazioni')
      }
    },
    formatDate(timestamp) {
      return new Date(timestamp).toLocaleString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    formatStatus(status) {
      if (status === 'pagato') return this.$t('nuovo') || 'Nuovo';
      if (status === 'in_preparazione') return this.$t('preparazione');
      if (status === 'pronto') return this.$t('pronto');
      if (status === 'consegnato') return this.$t('consegnato');
      return status;
    },
    formatDataRitiro(dateStr) {
      if (!dateStr) return '';
      // Assumes YYYY-MM-DD
      const [year, month, day] = dateStr.split('-');
      return `${day}/${month}/${year}`;
    }
  },
  beforeMount() {
    this.fetchOrdini()
    this.fetchSettings()
    
    if (this.store && this.store.socket) {
      this.store.socket.on('ricevutoOrdineAsporto', () => {
        console.log('📡 GestioneAsporto received socket update');
        this.fetchOrdini();
      });
    }

    // Poll for new orders every 30 seconds
    this.intervalId = setInterval(this.fetchOrdini, 30000)
  },
  beforeUnmount() {
    if (this.intervalId) clearInterval(this.intervalId)
  }
}
</script>

<style scoped>
.gestione-asporto {
  height: 100vh;
  overflow-y: auto;
  background: #f0f2f5;
  padding: 20px;
  box-sizing: border-box;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  background: white;
  padding: 15px 20px;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
}

.header h1 {
  margin: 0;
  color: #1a1a2e;
  font-size: 1.5rem;
}

.btn-back {
  color: #666;
  text-decoration: none;
  font-weight: bold;
}

.btn-refresh {
  background: #f0f2f5;
  border: none;
  padding: 8px 16px;
  border-radius: 20px;
  cursor: pointer;
  font-weight: bold;
  color: #1a1a2e;
}

.filters {
  margin-bottom: 20px;
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  align-items: center;
}

.filters select {
  padding: 10px 20px;
  border-radius: 8px;
  border: 1px solid #ddd;
  font-size: 1rem;
  background: white;
}

.date-filter {
  display: flex;
  align-items: center;
  gap: 10px;
}

.date-filter input {
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #ddd;
  font-size: 1rem;
}

.orders-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
}

.order-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.05);
  border-left: 5px solid #ddd;
}

.order-card.status-pagato { border-left-color: #3498db; }
.order-card.status-in_preparazione { border-left-color: #f1c40f; }
.order-card.status-pronto { border-left-color: #2ecc71; }
.order-card.status-consegnato { border-left-color: #95a5a6; opacity: 0.8; }

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
}

.order-info h3 {
  margin: 0 0 5px 0;
  color: #1a1a2e;
}

.contact {
  color: #666;
  font-size: 0.9rem;
}

.ritiro-info {
  margin-top: 5px;
  background: #e8f5e9;
  color: #2e7d32;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.9rem;
  display: inline-block;
}

.order-time {
  color: #999;
  font-size: 0.85rem;
}

.item-row {
  display: flex;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.qty {
  font-weight: bold;
  margin-right: 10px;
  color: #1a1a2e;
}

.extras {
  width: 100%;
  font-size: 0.85rem;
  color: #27ae60;
  margin-left: 25px;
}

.extras.without {
  color: #c0392b;
}

.order-footer {
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #f0f0f0;
}

.total {
  font-weight: bold;
  font-size: 1.2rem;
  margin-bottom: 15px;
  text-align: right;
  color: #1a1a2e;
}

.actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.current-status {
  font-weight: bold;
  font-size: 0.9rem;
  padding: 5px 10px;
  border-radius: 4px;
  background: #f0f2f5;
  color: #666;
}

.current-status.pagato { color: #3498db; background: rgba(52, 152, 219, 0.1); }
.current-status.in_preparazione { color: #f1c40f; background: rgba(241, 196, 15, 0.1); }
.current-status.pronto { color: #2ecc71; background: rgba(46, 204, 113, 0.1); }

.btn-status {
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  color: white;
  font-weight: bold;
  cursor: pointer;
}

.btn-status.prep { background: #f1c40f; color: #333; }
.btn-status.ready { background: #2ecc71; }
.btn-status.done { background: #95a5a6; }

.error-message {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: #ff4444;
  color: white;
  padding: 10px 20px;
  border-radius: 50px;
}

.current-status.clickable {
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.current-status.clickable:hover {
  transform: scale(1.05);
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
}

.edit-hint {
  opacity: 0.5;
  font-size: 1.1rem;
}

.current-status.clickable:hover .edit-hint {
  opacity: 1;
}

.status-select {
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #ddd;
  font-size: 0.9rem;
  background: white;
  color: #1a1a2e;
  cursor: pointer;
  font-weight: bold;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.btn-create {
  background: #2ecc71;
  border: none;
  padding: 8px 16px;
  border-radius: 20px;
  cursor: pointer;
  font-weight: bold;
  color: white;
  transition: all 0.2s;
}

.btn-create:hover {
  background: #27ae60;
  transform: scale(1.05);
}

.btn-settings {
  background: white;
  border: 1px solid #ddd;
  padding: 8px 16px;
  border-radius: 20px;
  cursor: pointer;
  font-weight: bold;
  color: #666;
  transition: all 0.2s;
}

.btn-settings:hover {
  background: #f8f9fa;
  border-color: #ccc;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.settings-modal {
  background: white;
  width: 90%;
  max-width: 500px;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.2);
  overflow: hidden;
}

.modal-header {
  padding: 20px;
  background: #1a1a2e;
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.25rem;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
}

.modal-body {
  padding: 25px;
}

.settings-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  margin-bottom: 30px;
}

.setting-group h3 {
  margin: 0 0 15px 0;
  font-size: 1rem;
  color: #1a1a2e;
  border-bottom: 2px solid #f0f2f5;
  padding-bottom: 5px;
}

.input-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.order-time {
  font-size: 0.85rem;
  color: #888;
}

.order-badges {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 5px;
}

.badge-staff {
  background: #3498db;
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: bold;
}

.badge-asporto {
  background: #2ecc71;
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: bold;
}

.badge-delivery {
  background: #e74c3c;
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: bold;
}

.delivery-info {
  margin-top: 10px;
  background: #fff3e0;
  color: #e65100;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.9rem;
  line-height: 1.4;
  border: 1px solid #ffe0b2;
}

.input-row label {
  font-size: 0.9rem;
  color: #666;
}

.input-row input {
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid #ddd;
  font-size: 0.9rem;
}

.modal-footer {
  text-align: right;
  border-top: 1px solid #f0f2f5;
  padding-top: 20px;
}

.btn-save {
  background: #2ecc71;
  color: white;
  border: none;
  padding: 10px 25px;
  border-radius: 8px;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-save:hover {
  background: #27ae60;
}

.day-selector-section {
  margin-bottom: 20px;
}

.giorno-label {
  display: block;
  margin-bottom: 6px;
  font-size: 0.95rem;
  color: #1a1a2e;
}

.giorno-select {
  width: 100%;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #ddd;
  font-size: 0.95rem;
  background-color: #fff;
  color: #333;
}

.giorno-attivo-row {
  margin-bottom: 20px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  color: #1a1a2e;
  cursor: pointer;
}

.disabled-grid {
  opacity: 0.4;
  pointer-events: none;
}
</style>
